import { ShipmentService } from '../../data/shipment.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AppState } from '../index';
import { EntityOp, MergeStrategy, ofEntityOp, ofEntityType } from '@ngrx/data';
import { debounceTime, filter, map, mergeMap, take, tap, withLatestFrom, catchError } from 'rxjs/operators';
import {
    selectCanRecordUndo,
    selectShipmentEditable,
    selectShipmentEditorId,
    selectShipmentEditorOptions,
    selectShipmentEditorSaving,
    selectShipmentEditorShipment,
    selectShipmentEditorState,
    selectShipmentEditorUnsaved,
    selectShipmentEditorValidationMode,
    selectShipmentEditorViolations
} from './shipment-editor.state';
import { selectQuotingEditorActive } from '../quote-progress/quote-progress.state';
import { quotingCompleted, quotingStart } from '../quote-progress/quote-progress.actions';
import {
    shipmentEditorAddCommodity,
    shipmentEditorAddCommodityFromTemplate,
    shipmentEditorAddCourierOption,
    shipmentEditorAddEmptyCommodity,
    shipmentEditorAddEmptyPackage,
    shipmentEditorAddPackage,
    shipmentEditorAddPackageFromTemplate,
    shipmentEditorAddReference,
    shipmentEditorChangeDetected,
    shipmentEditorCreateReference,
    shipmentEditorDeleteCommodity,
    shipmentEditorDeletePackage,
    shipmentEditorDeletePackages,
    shipmentEditorDisplayEditor,
    shipmentEditorDisplayQuotes,
    shipmentEditorGetQuotes,
    shipmentEditorLoadFailure,
    shipmentEditorLoadRequest,
    shipmentEditorLoadSuccess,
    shipmentEditorMoveCommodities,
    shipmentEditorPatchCommodities,
    shipmentEditorPatchForms,
    shipmentEditorPatchOptions,
    shipmentEditorPatchPackages,
    shipmentEditorPatchReferences,
    shipmentEditorRecordUndo,
    shipmentEditorRedo,
    shipmentEditorRemoveCourierOption,
    shipmentEditorRemoveReference,
    shipmentEditorSaveFailure,
    shipmentEditorSaveRequest,
    shipmentEditorSaveSuccess,
    shipmentEditorSetActiveOptions,
    shipmentEditorSetCurrency,
    shipmentEditorSetInsurance,
    shipmentEditorSetOption,
    shipmentEditorSetOptionData,
    shipmentEditorSetPackagingType,
    shipmentEditorSetShipmentDate,
    shipmentEditorSetSignatureOption, shipmentEditorSetValidationMode,
    shipmentEditorSetViolations,
    shipmentEditorSetWeightUnit,
    shipmentEditorDocumentUploadSuccess,
    shipmentEditorOpenDocumentUploadModal,
    shipmentEditorUndo,
    shipmentEditorUpdate,
    shipmentEditorUpdateDestination,
    shipmentEditorUpdateOptionsSection,
    shipmentEditorUpdatePackageSection,
    shipmentEditorUpdateShipper,
    shipmentEditorValidateQuotes,
    shipmentEditorDeleteDocumentRequest,
    shipmentEditorDeleteDocumentSuccess,
    shipmentEditorDeleteDocumentFailure
} from './shipment-editor.actions';
import { Injectable } from '@angular/core';
import { selectReferenceDefinitions, selectSelectableCourierOptions } from '../profile/profile.state';
import { ShipmentValidatorService } from '../../modules/shared/services/shipment-validator.service';
import { Clock } from '../../utils/clock';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ShipmentState as ShipmentStateUtil } from '../../utils/shipment-state';
import { shipmentHash } from '../../utils/shipment-hash';
import { MatDialog } from '@angular/material/dialog';
import { PackageSelectComponent } from '../../modules/package-library/package-select/package-select.component';
import { CommoditySelectComponent } from '../../modules/package-library/commodity-select/commodity-select.component';
import { ViolationsModalComponent } from '../../modules/shipment/violations-modal/violations-modal.component';
import { selectRouteParamDeep } from '../router/router.state';
import { Router } from '@angular/router';
import { ValidationMode } from '../../enum/validation-mode.enum';
import { equal } from '../../models/violation';
import { ShipmentDocumentUploadModalComponent } from '../../modules/shipment/shipment-document-upload-modal/shipment-document-upload-modal.component';
import { DocumentService } from '../../modules/shared/services/document.service';
import { of } from 'rxjs';

@Injectable()
export class ShipmentEditorEffects {

    constructor(private actions$: Actions, private store: Store<AppState>, private shipmentService: ShipmentService,
                private snackbar: MatSnackBar, private validator: ShipmentValidatorService, private dialog: MatDialog,
                private router: Router, private documentService: DocumentService) {
    }

    shipmentEntityLoader$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.QUERY_BY_KEY_SUCCESS ]),
        withLatestFrom(this.store.pipe(select(selectShipmentEditorId))),
        filter(([ action, id ]) => action.payload.data.id === id),
        map(([ action, id ]) => shipmentEditorLoadSuccess({ shipment: action.payload.data }))
    ));

    shipmentLoadRequest$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorLoadRequest),
        tap((action) => this.shipmentService.getByKey(action.id))
    ), { dispatch: false });

    shipmentLoadError$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.QUERY_BY_KEY_ERROR ]),
        tap((action) => this.snackbar.open($localize`We are having some difficulty communicating with the server and cannot load your shipment.  Please try again later.`)),
        map(() => shipmentEditorLoadFailure())
    ));

    shipmentLoadSuccess$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.QUERY_BY_KEY_SUCCESS ]),
        withLatestFrom(this.store.select(selectShipmentEditorId)),
        filter(([ action, id ]) => action.payload.data.id === id && ShipmentStateUtil.isQuotable(action.payload.data.statusCode)),
        map(() => shipmentEditorSaveSuccess())
    ));

    shipmentLoadUpdateCache$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.QUERY_BY_KEY_SUCCESS ]),
        tap((action) => this.shipmentService.updateOneInCache(action.payload.data, { mergeStrategy: MergeStrategy.OverwriteChanges }))
    ), { dispatch: false });

    shipmentCheckDate$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorLoadSuccess),
        withLatestFrom(this.store.select(selectShipmentEditorShipment)),
        filter(([ _, shipment ]) => shipment.shipmentDate < Clock.nowIso()),
        map(() => shipmentEditorSetShipmentDate({ shipmentDate: Clock.nowIso() })),
    ));

    /* - we don't need to add a package automatically
    shipmentCheckPackages$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorLoadSuccess),
        withLatestFrom(this.store.select(selectShipmentEditorShipment)),
        filter(([ _, shipment ]) => shipment.packages.length === 0),
        filter(([ _, shipment ]) => ShipmentStateUtil.isQuotable(shipment.statusCode)),
        map(() => shipmentEditorAddPackage()),
    ));
        */

    shipmentAddReference$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorAddReference),
        withLatestFrom(this.store.pipe(select(selectReferenceDefinitions))),
        map(([ , modification ]) => shipmentEditorCreateReference({ modification }))
    ));

    shipmentUndoWatcher$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorChangeDetected),
        withLatestFrom(this.store.select(selectCanRecordUndo)),
        filter(([ _, canRecord ]) => canRecord),
        map(() => shipmentEditorRecordUndo())
    ));

    shipmentChangeDetection$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorUpdateShipper, shipmentEditorUpdateDestination, shipmentEditorUpdateOptionsSection,
            shipmentEditorUpdatePackageSection, shipmentEditorPatchOptions, shipmentEditorPatchReferences, shipmentEditorPatchForms,
            shipmentEditorSetInsurance, shipmentEditorAddCourierOption, shipmentEditorRemoveCourierOption,
            shipmentEditorAddReference, shipmentEditorRemoveReference, shipmentEditorUpdate,
            shipmentEditorSetWeightUnit, shipmentEditorSetSignatureOption, shipmentEditorSetCurrency,
            shipmentEditorSetPackagingType, shipmentEditorAddEmptyPackage, shipmentEditorAddPackageFromTemplate,
            shipmentEditorDeletePackage, shipmentEditorAddEmptyCommodity,
            shipmentEditorDeleteCommodity, shipmentEditorSetOptionData, shipmentEditorAddCommodityFromTemplate,
            shipmentEditorSetShipmentDate, shipmentEditorUndo, shipmentEditorRedo, shipmentEditorMoveCommodities,
            shipmentEditorPatchPackages, shipmentEditorPatchCommodities, shipmentEditorAddCommodity, shipmentEditorDeleteCommodity,
            shipmentEditorSetOption, shipmentEditorDeletePackages),
        mergeMap(() => this.store.select(selectShipmentEditorState)),
        debounceTime(1000),
        tap(() => console.log('undo watch 1')),
        filter(state => ShipmentStateUtil.isQuotable(state.shipment.statusCode)),
        tap(() => console.log('undo watch 2')),
        filter(state => shipmentHash(state.shipment) !== state.ignoreValueHash),
        tap(state => console.log(shipmentHash(state.shipment) + ' => ' + state.ignoreValueHash)),
        map(() => shipmentEditorChangeDetected())
    ));

    shipmentSaveRequest$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorSaveRequest),
        withLatestFrom(this.store.select(selectShipmentEditorShipment), this.store.select(selectShipmentEditable)),
        filter(([ , , editable ]) => editable),
        tap(([ , shipment, ]) => this.shipmentService.update(shipment))
    ), { dispatch: false });

    shipmentSaveSuccess$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.SAVE_UPDATE_ONE_SUCCESS ]),
        map(() => shipmentEditorSaveSuccess())
    ));

    shipmentSaveError$ = createEffect(() => this.actions$.pipe(
        ofEntityType('Shipment'),
        ofEntityOp([ EntityOp.SAVE_UPDATE_ONE_ERROR ]),
        tap((action) => console.log(action)),
        tap((action) => this.snackbar.open($localize`We are having difficulty communicating with the server and cannot save your changes right now.  Please try again later.`)),
        map(() => shipmentEditorSaveFailure())
    ));

    shipmentSaveRetry$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorSaveFailure),
        debounceTime(30000),
        withLatestFrom(this.store.select(selectShipmentEditorUnsaved)),
        filter(([ , valid ]) => (valid)),
        map(() => shipmentEditorSaveRequest())
    ));

    shipmentUpdateClearQuotes$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorUpdate),
        withLatestFrom(this.store.select(selectShipmentEditorId)),
        tap(([ , id ]) => this.shipmentService.cacheQuotes(id, []))
    ), { dispatch: false });

    shipmentSaveOnChange$ = createEffect(() => this.store.pipe(
        select(selectShipmentEditorUnsaved),
        filter(unsaved => unsaved),
        debounceTime(5000),
        withLatestFrom(this.store.select(selectShipmentEditorSaving), this.store.select(selectQuotingEditorActive)),
        filter(([ unsaved, saving, quoting ]) => unsaved && !saving && !quoting),
        map(() => shipmentEditorSaveRequest())
    ));

    shipmentSaveAfterQuoting$ = createEffect(() => this.actions$.pipe(
        ofType(quotingCompleted),
        withLatestFrom(this.store.select(selectShipmentEditorUnsaved), this.store.select(selectShipmentEditorSaving)),
        filter(([ , unsaved, saving ]) => unsaved && !saving),
        map(() => shipmentEditorSaveRequest())
    ));

    /*
    shipmentViolationCheck$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorChangeDetected, shipmentEditorLoadSuccess),
        withLatestFrom(this.store.select(selectShipmentEditorShipment), this.store.select(selectShipmentEditorViolations)),
        tap(([ , shipment, violations ]) => this.validator.updateViolations(shipment, violations)),
    ), { dispatch: false });
    */

    shipmentValidation$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorChangeDetected, shipmentEditorLoadSuccess, shipmentEditorValidateQuotes, shipmentEditorSetValidationMode),
        withLatestFrom(this.store.select(selectShipmentEditorShipment), this.store.select(selectShipmentEditorViolations),
            this.store.select(selectShipmentEditorValidationMode)),
        map(([ , shipment, existingViolations, mode ]) =>
            [ existingViolations, this.validator.validateEditorShipment(shipment, mode)] ),
        filter(([ existingViolations, newViolations ]) => !equal(existingViolations, newViolations)),
        map(([ , violations ]) => shipmentEditorSetViolations({ violations }))
    ));

    shipmentEditorSetActiveOptions = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorSetActiveOptions),
        withLatestFrom(
            this.store.select(selectSelectableCourierOptions),
            this.store.select(selectShipmentEditorOptions)
        ),
        map(([ action, definitions, options ]) => {
            const shipmentOptions = { ...options };
            definitions.forEach(def => {
                if (action.options.indexOf(def.code) === -1) {
                    delete shipmentOptions[def.code];
                } else {
                    shipmentOptions[def.code] = '1';
                    def.parameters.forEach(param => {
                        if (shipmentOptions[param.code] === undefined) {
                            shipmentOptions[param.code] = param.default;
                        }
                    });
                }
            });
            return shipmentEditorSetOptionData({ courierOptions: shipmentOptions });
        })
    ));

    shipmentEditorAddPackage$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorAddPackage),
        tap(() => this.dialog.open(PackageSelectComponent, { panelClass: 'modal-editor' }))
    ), { dispatch: false });

    shipmentEditorAddCommodity$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorAddCommodity),
        tap((action) =>
            this.dialog.open(CommoditySelectComponent, { panelClass: 'modal-editor', data: { packageIndex: action.packageIndex } }))
    ), { dispatch: false });

    navigateToQuotes$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(shipmentEditorDisplayQuotes),
                withLatestFrom(this.store.select(selectRouteParamDeep('id'))),
                tap(([ _, id ]) => {
                    this.router.navigate([ '/shipment', id, 'quotes' ]);
                })
            ),
        { dispatch: false }
    );

    navigateToEditor$ = createEffect(() => this.actions$.pipe(
            ofType(shipmentEditorDisplayEditor),
            withLatestFrom(this.store.select(selectRouteParamDeep('id'))),
            tap(([ _, id ]) => {
                this.router.navigate([ '/shipment', id, 'edit' ]);
            })
        ),
        { dispatch: false }
    );

    getQuotes$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorGetQuotes),
        mergeMap(() => {
            this.store.dispatch(shipmentEditorValidateQuotes());
            return this.store.select(selectShipmentEditorId).pipe(
                withLatestFrom(this.store.select(selectShipmentEditorValidationMode), this.store.select(selectShipmentEditorShipment)),
                take(1),
                tap(([ id, mode, shipment ]) => {
                    if (mode === ValidationMode.NONE) {
                        mode = ValidationMode.QUOTE;
                    }
                    const violations = this.validator.validateEditorShipment(shipment, mode);
                    if (violations.length > 0) {
                        this.dialog.open(ViolationsModalComponent);
                    } else {
                        this.store.dispatch(quotingStart({ id }));
                        this.store.dispatch(shipmentEditorDisplayQuotes());
                    }
                })
            );
        })
    ), { dispatch: false });

    openDocumentUploadModal$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorOpenDocumentUploadModal),
        withLatestFrom(this.store.select(selectShipmentEditorId)),
        filter(([ , shipmentId ]) => !!shipmentId),
        mergeMap(([ action, shipmentId ]) => this.dialog.open(ShipmentDocumentUploadModalComponent, {
            width: '560px',
            maxWidth: '95vw',
            data: { shipmentId, file: action.file }
        }).afterClosed().pipe(
            filter(result => !!result?.success),
            map(() => shipmentEditorDocumentUploadSuccess())
        ))
    ));

    reloadShipmentAfterDocumentUpload$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorDocumentUploadSuccess),
        withLatestFrom(this.store.select(selectShipmentEditorId)),
        filter(([ , shipmentId ]) => !!shipmentId),
        tap(() => this.snackbar.open($localize`Document uploaded successfully.`)),
        map(([ , shipmentId ]) => shipmentEditorLoadRequest({ id: shipmentId }))
    ));

    deleteDocument$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorDeleteDocumentRequest),
        mergeMap(({ documentId }) =>
            this.documentService.deleteDocument(documentId).pipe(
                map(() => shipmentEditorDeleteDocumentSuccess({ documentId })),
                catchError(() => of(shipmentEditorDeleteDocumentFailure({ documentId })))
            )
        )
    ));

    deleteDocumentSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorDeleteDocumentSuccess),
        tap(() => this.snackbar.open($localize`Document deleted successfully.`))
    ), { dispatch: false });

    deleteDocumentFailure$ = createEffect(() => this.actions$.pipe(
        ofType(shipmentEditorDeleteDocumentFailure),
        tap(() => this.snackbar.open($localize`Failed to delete document. Please try again.`))
    ), { dispatch: false });
}
